import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiError } from '../lib/api';
import type { User } from '../lib/types';

type AuthState = {
  user: User | null;
  /** True until the initial session check resolves — gates the whole app. */
  loading: boolean;
  /** Set when the session check itself fails (server down), not on 401. */
  bootError: string | null;
  /** True right after a fresh login, so the dashboard plays the brand reveal once. */
  justAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearJustAuthenticated: () => void;
  retryBoot: () => void;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [bootError, setBootError] = useState<string | null>(null);
  const [justAuthenticated, setJustAuthenticated] = useState(false);
  const [bootNonce, setBootNonce] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setBootError(null);
      try {
        const { user: me } = await api.me(controller.signal);
        if (!controller.signal.aborted) setUser(me);
      } catch (err) {
        if (controller.signal.aborted) return;
        // A 401 just means "not signed in" — that is not an error state.
        if (err instanceof ApiError && err.status !== 401) {
          setBootError(err.message);
        }
        setUser(null);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [bootNonce]);

  const login = useCallback(async (email: string, password: string) => {
    const { user: me } = await api.login({ email, password });
    setUser(me);
    setJustAuthenticated(true);
  }, []);

  const signup = useCallback(async (name: string, email: string, password: string) => {
    const { user: me } = await api.signup({ name, email, password });
    setUser(me);
    setJustAuthenticated(true);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      // Clear locally even if the request failed — the user asked to leave.
      setUser(null);
      setJustAuthenticated(false);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      bootError,
      justAuthenticated,
      login,
      signup,
      logout,
      clearJustAuthenticated: () => setJustAuthenticated(false),
      retryBoot: () => setBootNonce((n) => n + 1),
    }),
    [user, loading, bootError, justAuthenticated, login, signup, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an AuthProvider');
  return ctx;
}
